import parsePhoneNumberFromString, { CountryCode, getCountryCallingCode } from "libphonenumber-js";

const isLocalOrStagingEnv =
  typeof process !== "undefined" && process.env && ["local", "staging", "test"].includes(process.env["SERVER_CONTEXT"] || "");

export class PhoneNumberHelper {
  static isPhoneNumberValid(phoneNumber?: string, countryCodes?: string[], orgId?: number): string | undefined {
    if (typeof phoneNumber !== "string") {
      phoneNumber = [null, undefined].includes(phoneNumber) ? "" : String(phoneNumber);
    }
    phoneNumber = phoneNumber.trim();

    if (!phoneNumber) {
      return undefined;
    }
    const countries: CountryCode[] = countryCodes && countryCodes.length ? (countryCodes as CountryCode[]) : (["US"] as CountryCode[]);
    let validPhoneNumber: string | undefined = undefined;

    for (let i = 0; i < countries.length; i++) {
      const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber, countries[i]);

      if (parsedPhoneNumber && parsedPhoneNumber.isValid()) {
        validPhoneNumber = parsedPhoneNumber.number as string;

        return validPhoneNumber;
      }

      // For local and staging environments, allow test numbers (starting from 555)
      if (isLocalOrStagingEnv && parsedPhoneNumber?.isPossible()) {
        return (parsedPhoneNumber?.number as string) || phoneNumber;
      }

      // Check if for this specific organization we allow usage of test numbers (starting from 555)
      if (orgId) {
        const allowTestPhonesForOrgIds = process.env["ALLOW_TEST_PHONES_FOR_ORG_IDS"]?.split(",").map((orgId) => Number(orgId?.trim())) || [];
        if (allowTestPhonesForOrgIds.includes(orgId) && parsedPhoneNumber?.isPossible()) {
          return parsedPhoneNumber?.number as string;
        }
      }
    }

    return validPhoneNumber;
  }

  static normalizePhoneNumber(phoneNumber: string, countryCodes?: string[], orgId?: number): string | undefined {
    return PhoneNumberHelper.isPhoneNumberValid(phoneNumber, countryCodes, orgId);
  }

  /**
   * Format a stored number for display in national style, e.g.
   * "+17193576035" → "(719) 357-6035".
   *
   * Display-only, and deliberately forgiving: falls back to the trimmed input
   * when the number can't be parsed, so numbers saved before normalization
   * still render as whatever the operator originally typed rather than
   * vanishing. Uses `isPossible` rather than `isValid` so test numbers (555…)
   * still format.
   *
   * Only numbers sharing `countryCode`'s calling code are shown in national
   * style. Anything foreign keeps its international "+44 7911 123456" form,
   * since national style would strip the country code and leave it undialable.
   * Compared by calling code rather than `parsed.country`, which is undefined
   * for unassigned-but-possible numbers like the 555… test range.
   */
  static formatPhoneNumberNational(phoneNumber?: string, countryCode: CountryCode = "US" as CountryCode): string {
    const raw = (phoneNumber ?? "").trim();

    if (!raw) {
      return "";
    }

    try {
      const parsed = parsePhoneNumberFromString(raw, countryCode);

      if (!parsed?.isPossible()) {
        return raw;
      }

      return parsed.countryCallingCode === getCountryCallingCode(countryCode) ? parsed.formatNational() : parsed.formatInternational();
    } catch {
      return raw;
    }
  }

  /**
   * Dialable numbers from a free-form phone field. Operators sometimes type more
   * than one number into a single field (Facility Phone: "813-555-0100, 727-555-0100"),
   * which read as one number glues the digits into something undialable. Splits on
   * commas, semicolons, slashes, pipes, new lines and the words "or" / "and" / "&";
   * each piece becomes E.164 when it parses (isPossible, so 555 test numbers pass),
   * else its bare digits. Pieces too short to be a number (e.g. "ext 12") are dropped.
   * Order is kept, so callers that can only dial one number take the first.
   */
  static toDialableNumbers(raw: string | null | undefined, countryCode: CountryCode = "US" as CountryCode): string[] {
    const numbers = (raw ?? "")
      // Separators between numbers: comma, semicolon, pipe, newline, or a spaced "/", "or", "and", "&".
      // A bare "/" inside one number ("813/555-0100") is part of that number, not a separator.
      .split(/[,;|\n]|\s+(?:\/|or|and|&)\s+/i)
      .map((piece) => piece.trim())
      .filter((piece) => piece.replace(/\D/g, "").length >= 7)
      .map((piece) => {
        const parsed = parsePhoneNumberFromString(piece, countryCode);
        return parsed?.isPossible() ? (parsed.number as string) : piece.replace(/[^\d+]/g, "");
      });
    return [...new Set(numbers)];
  }

  static formatPhoneNumber(phoneNumber?: string, countryCodes?: string[], orgId?: number): string | undefined {
    const normalizedPhoneNumber = PhoneNumberHelper.isPhoneNumberValid(phoneNumber, countryCodes, orgId);

    if (!normalizedPhoneNumber) {
      return undefined;
    }

    const parsedPhoneNumber = parsePhoneNumberFromString(normalizedPhoneNumber);

    if (!parsedPhoneNumber) {
      return normalizedPhoneNumber;
    }

    return parsedPhoneNumber.formatInternational();
  }
}
